import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, AlertCircle } from 'lucide-react';

interface Prediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface StructuredAddress {
  formatted_address: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  postal_code?: string;
  country?: string;
  lat?: number;
  lng?: number;
}

interface PlacesAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (address: string, structuredAddress?: StructuredAddress) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  onImageFound?: (imageUrl: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  allowManualInput?: boolean;
}

const normalizeAddress = (address: string): string => {
  return address.toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[,\.]/g, '')
    .trim();
};

const calculateSimilarity = (str1: string, str2: string): number => {
  const s1 = normalizeAddress(str1);
  const s2 = normalizeAddress(str2);
  
  // Enhanced similarity calculation
  const words1 = s1.split(' ');
  const words2 = s2.split(' ');
  
  let matches = 0;
  let weightedTotal = 0;
  
  // Weight important parts more heavily
  for (const word of words1) {
    if (words2.includes(word)) {
      matches++;
      // Give more weight to numbers and street names
      if (/\d+/.test(word)) weightedTotal += 2; // Street numbers
      else if (/(street|st|avenue|ave|road|rd|lane|ln|drive|dr|circle|cir|court|ct|boulevard|blvd|way|place|pl)$/i.test(word)) {
        weightedTotal += 1.5; // Street types
      } else {
        weightedTotal += 1; // Regular words
      }
    }
  }
  
  // Calculate final similarity score (0 to 1)
  const maxPossibleScore = Math.max(words1.length, words2.length) * 2; // Maximum possible weighted score
  return weightedTotal / maxPossibleScore;
};

const findBestMatch = (input: string, predictions: Prediction[]): { prediction: Prediction; confidence: number } | null => {
  if (!predictions.length) return null;
  
  let bestMatch: Prediction | null = null;
  let bestScore = 0;
  
  for (const prediction of predictions) {
    const score = calculateSimilarity(input, prediction.description);
    if (score > bestScore) {
      bestScore = score;
      bestMatch = prediction;
    }
  }
  
  // Return both the match and confidence score if above threshold
  return bestScore > 0.3 ? { prediction: bestMatch!, confidence: bestScore } : null;
};

const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = 'Enter address',
  disabled = false,
  error = false,
  onImageFound,
  onFocus,
  onBlur,
  allowManualInput = true
}) => {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [manualInputTimer, setManualInputTimer] = useState<NodeJS.Timeout | null>(null);

  // Get place predictions
  const getPlacePredictions = async (input: string) => {
    if (!input.trim()) {
      setPredictions([]);
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/places`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'x-client-info': 'places-autocomplete'
        },
        body: JSON.stringify({ input })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch predictions');
      }

      const data = await response.json();

      if (data?.predictions) {
        setPredictions(data.predictions);
        setIsOpen(true);
      } else {
        setPredictions([]);
      }
    } catch (error) {
      console.error('Places API error:', error instanceof Error ? error.message : error);
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Get place details for a selected place
  const getPlaceDetails = async (placeId: string): Promise<StructuredAddress | null> => {
    try {
      console.log('Getting place details for:', placeId);
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/places`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'x-client-info': 'places-details'
        },
        body: JSON.stringify({ place_id: placeId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch place details');
      }

      const data = await response.json();
      
      if (data?.status === 'OK' && data?.result) {
        console.log('Place details received:', data.result);
        
        // Map Google Places API response to our database structure
        const components = data.result.address_components || [];
        const geometry = data.result.geometry;
        
        let streetNumber = '';
        let route = '';
        let subpremise = '';
        let city = '';
        let state = '';
        let zipCode = '';
        let country = '';
        
        // Extract components from Google's response
        components.forEach((component: any) => {
          const types = component.types;
          
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          } else if (types.includes('route')) {
            route = component.long_name;
          } else if (types.includes('subpremise')) {
            subpremise = component.long_name;
          } else if (types.includes('locality')) {
            city = component.long_name;
          } else if (types.includes('administrative_area_level_1')) {
            state = component.short_name;
          } else if (types.includes('postal_code')) {
            zipCode = component.long_name;
          } else if (types.includes('country')) {
            country = component.short_name;
          }
        });
        
        // Construct address_line_1 (street number + route)
        const addressLine1 = `${streetNumber} ${route}`.trim();
        
        // address_line_2 would be subpremise (apartment, suite, etc.)
        const addressLine2 = subpremise;
        
        const structuredAddress: StructuredAddress = {
          formatted_address: data.result.formatted_address,
          address_line_1: addressLine1,
          address_line_2: addressLine2,
          city: city,
          state: state,
          zip_code: zipCode,
          postal_code: zipCode,
          country: country || 'US',
          lat: geometry?.location?.lat,
          lng: geometry?.location?.lng
        };
        
        console.log('Structured address mapped:', structuredAddress);
        return structuredAddress;
      }
      
      return null;
    } catch (error) {
      console.error('Place details error:', error instanceof Error ? error.message : error);
      return null;
    }
  };

  // Handle address selection
  const handleSelectAddress = async (prediction: Prediction) => {
    console.log('Selected prediction:', prediction);
    
    // Get detailed place information
    const structuredAddress = await getPlaceDetails(prediction.place_id);
    
    // Call onSelect with both the formatted address and structured data
    onSelect(prediction.description, structuredAddress || undefined);
    
    setIsOpen(false);
    setPredictions([]);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Enhance handleChange to handle manual input
  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onChange(value);
    
    // Clear any existing timer
    if (manualInputTimer) {
      clearTimeout(manualInputTimer);
    }
    
    if (value.length > 2) {
      // Get predictions as before
      const timer = setTimeout(async () => {
        await getPlacePredictions(value);
        
        // If manual input is allowed and we have predictions, try to find a match
        if (allowManualInput && predictions.length > 0) {
          const matchResult = findBestMatch(value, predictions);
          if (matchResult) {
            const { prediction, confidence } = matchResult;
            console.log('Found potential match:', prediction.description, 'with confidence:', confidence);
            
            // If confidence is very high (> 0.8), automatically select it
            if (confidence > 0.8) {
              console.log('Auto-selecting high confidence match');
              handleSelectAddress(prediction);
            } else {
              // Otherwise show it as a suggestion
              setPredictions([prediction, ...predictions.filter(p => p.place_id !== prediction.place_id)]);
            }
          }
        }
      }, 300);
      
      setManualInputTimer(timer);
      return () => clearTimeout(timer);
    }
  };

  // Add a method to handle manual submission
  const handleManualSubmit = async () => {
    if (!value.trim() || !allowManualInput) return;
    
    try {
      // Always try to get fresh predictions from Google Places API
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/places`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'x-client-info': 'places-autocomplete'
        },
        body: JSON.stringify({ input: value })
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.predictions?.length > 0) {
          // Find best match with confidence score
          const matchResult = findBestMatch(value, data.predictions);
          
          if (matchResult) {
            const { prediction, confidence } = matchResult;
            console.log('Found match:', prediction.description, 'with confidence:', confidence);
            
            // Get place details for high confidence matches
            if (confidence > 0.6) {
              const placeDetails = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/places`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                  'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
                  'x-client-info': 'places-details'
                },
                body: JSON.stringify({ place_id: prediction.place_id })
              });

              if (placeDetails.ok) {
                const detailsData = await placeDetails.json();
                if (detailsData?.result) {
                  const components = detailsData.result.address_components || [];
                  const geometry = detailsData.result.geometry;
                  
                  let streetNumber = '';
                  let route = '';
                  let subpremise = '';
                  let city = '';
                  let state = '';
                  let zipCode = '';
                  let country = '';
                  
                  // Extract components from Google's response
                  components.forEach((component: any) => {
                    const types = component.types;
                    
                    if (types.includes('street_number')) {
                      streetNumber = component.long_name;
                    } else if (types.includes('route')) {
                      route = component.long_name;
                    } else if (types.includes('subpremise')) {
                      subpremise = component.long_name;
                    } else if (types.includes('locality')) {
                      city = component.long_name;
                    } else if (types.includes('administrative_area_level_1')) {
                      state = component.short_name;
                    } else if (types.includes('postal_code')) {
                      zipCode = component.long_name;
                    } else if (types.includes('country')) {
                      country = component.short_name;
                    }
                  });

                  const addressLine1 = `${streetNumber} ${route}`.trim();
                  const structuredAddress: StructuredAddress = {
                    formatted_address: detailsData.result.formatted_address,
                    address_line_1: addressLine1,
                    address_line_2: subpremise,
                    city: city,
                    state: state,
                    zip_code: zipCode,
                    postal_code: zipCode,
                    country: country || 'US',
                    lat: geometry?.location?.lat,
                    lng: geometry?.location?.lng
                  };
                  
                  // Use the matched prediction's description for high confidence matches
                  onSelect(confidence > 0.8 ? prediction.description : value, structuredAddress);
                  return;
                }
              }
            }
          }
        }
      }
      
      // If we get here, either confidence was low or something went wrong with the API calls
      console.log('Using manual parsing due to low confidence or API issues');
      const manuallyParsed: StructuredAddress = {
        formatted_address: value,
        address_line_1: value.split(',')[0]?.trim() || '',
        city: value.split(',')[1]?.trim() || '',
        state: value.split(',')[2]?.trim()?.split(' ')[0] || '',
        zip_code: value.split(',')[2]?.trim()?.split(' ')[1] || '',
        country: 'US'
      };
      
      onSelect(value, manuallyParsed);
    } catch (error) {
      console.error('Error processing manual input:', error);
      // Fall back to manual parsing if all else fails
      const manuallyParsed: StructuredAddress = {
        formatted_address: value,
        address_line_1: value.split(',')[0]?.trim() || '',
        city: value.split(',')[1]?.trim() || '',
        state: value.split(',')[2]?.trim()?.split(' ')[0] || '',
        zip_code: value.split(',')[2]?.trim()?.split(' ')[1] || '',
        country: 'US'
      };
      onSelect(value, manuallyParsed);
    }
  };

  // Add key press handler for Enter key
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleManualSubmit();
    }
  };

  // Handle input blur - close dropdown after a delay to allow for click events
  const handleBlur = () => {
    setTimeout(() => {
      setIsOpen(false);
      onBlur?.();
    }, 200);
  };

  // Handle input focus
  const handleFocus = () => {
    if (value.trim() && predictions.length > 0) {
      setIsOpen(true);
    }
    onFocus?.();
  };

  // Render dropdown content
  const renderDropdown = () => {
    if (!isOpen || predictions.length === 0) return null;

  return (
      <div className="places-autocomplete-dropdown absolute z-[150] bottom-full mb-4 w-full bg-white rounded-xl shadow-lg max-h-60 overflow-auto border border-gray-200" style={{ isolation: 'isolate' }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <ul className="py-2">
              {predictions.slice().reverse().map((prediction) => (
                <li
                  key={prediction.place_id}
                  onClick={() => handleSelectAddress(prediction)}
                  className="px-4 py-2.5 hover:bg-gray-50 cursor-pointer flex items-start transition-colors"
                >
                  <MapPin className="w-5 h-5 mr-2 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-medium">{prediction.structured_formatting?.main_text}</div>
                    <div className="text-xs text-gray-500">{prediction.structured_formatting?.secondary_text}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
    );
  };

  return (
    <div className="relative w-full z-[150]">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          className={`search-input pl-10 ${error ? 'search-input--error' : ''}`}
          autoComplete="off"
        />
        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
          <MapPin className="w-5 h-5" />
        </div>
      </div>
      
      {renderDropdown()}
    </div>
  );
};

export default PlacesAutocomplete;